import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, InputSearch, Tag } from 'sonata-react-components';

import { SearchDropdownStyled } from './SearchDropdown.styled';

export type DropdownSuggestion = {
  extraData?: Record<string, any>;
  id: string;
  included?: boolean;
  subtitle?: string;
  tagText?: string;
  title: string;
};

type Props = {
  classNames?: {
    searchInput?: string;
    suggestionsContainer?: string;
    suggestionsItemContent?: string;
  };
  inputAdd?: boolean;
  isLoading?: boolean;
  isReplace?: boolean;
  lens?: boolean;
  limit?: number;
  onClickAdd?: () => void;
  onSearch?: (value: string) => void;
  onSuggestionSelect: (suggestion: DropdownSuggestion) => void;
  onTextChange: (value: string) => void;
  placeholder: string;
  prefixId: string;
  suggestions: DropdownSuggestion[];
};

export function SearchDropdown({
  classNames,
  inputAdd,
  isLoading,
  isReplace,
  lens,
  limit,
  onClickAdd,
  onSearch,
  onSuggestionSelect,
  onTextChange,
  placeholder,
  prefixId,
  suggestions
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [inputValue, setInputValue] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        Array.isArray(suggestions) &&
        suggestions.length > 0
      ) {
        setInputValue('');
        onTextChange('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onTextChange]);

  const handleTextChange = (value: string) => {
    setInputValue(value);
    if (!value) {
      onTextChange('');
    }
  };

  const handleFilter = (value: string) => {
    setInputValue(value);
    onTextChange(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSuggestionSelect = (suggestion: DropdownSuggestion) => {
    if (suggestion.included) {
      return;
    }
    setInputValue('');
    onTextChange('');
    onSuggestionSelect(suggestion);
  };

  const handleClickAdd = () => {
    if (onClickAdd) {
      onClickAdd();
      setInputValue('');
      onTextChange('');
    }
  };

  const showAddedTag = inputAdd && suggestions?.some?.(s => s.title === inputValue.trim());

  return (
    <SearchDropdownStyled $addButton={inputAdd} className="searchDropdown" ref={dropdownRef}>
      <div className="searchDropdown__search">
        <InputSearch
          className={cn('searchDropdown__searchInput', classNames?.searchInput)}
          disabled={isLoading}
          id={`${prefixId}-dropDownInput`}
          loading={isLoading}
          name="dropDown-inputSearch"
          onChange={handleTextChange}
          onFilter={handleFilter}
          placeholder={placeholder}
          tiny
          value={inputValue}
          {...(lens && { lens })}
        />
        {inputAdd && onClickAdd && !lens && (
          <div className="searchDropdown__searchActions">
            {showAddedTag ? (
              <div className="searchDropdown__searchActionsTag">
                <Tag id="searchDropdown-inputAdd-tag">
                  {formatMessage({
                    defaultMessage: 'Added',
                    id: 'common.added'
                  })}
                </Tag>
              </div>
            ) : (
              <ButtonUI
                disabled={!inputValue.trim()}
                icon={isReplace ? 'fal fa-exchange-alt' : 'fal fa-plus'}
                onClick={handleClickAdd}
                shape="link"
                text={formatMessage(
                  isReplace
                    ? {
                        defaultMessage: 'Replace',
                        id: 'common.replace'
                      }
                    : {
                        defaultMessage: 'Add New',
                        id: 'common.addNew'
                      }
                )}
                type="tertiary"
              />
            )}
          </div>
        )}
      </div>
      {!isLoading && inputValue && Array.isArray(suggestions) && suggestions.length > 0 && (
        <div className={cn('searchDropdown__suggestions', classNames?.suggestionsContainer)}>
          {suggestions.slice(0, limit).map(s => (
            <div className="searchDropdown__suggestionsItem" key={s.id} onClick={() => handleSuggestionSelect(s)}>
              <div className={cn('searchDropdown__suggestionsItemContent', classNames?.suggestionsItemContent)}>
                <div className="searchDropdown__suggestionsItemTitle" title={s.title}>
                  {s.title}
                </div>
                <div className="searchDropdown__suggestionsItemSubtitleContainer">
                  {s.subtitle && (
                    <div className="searchDropdown__suggestionsItemSubtitle" title={s.subtitle}>
                      {s.subtitle}
                    </div>
                  )}
                  {s.tagText && (
                    <Tag id={`searchDropdown-suggestion-tag-${s.tagText}`}>
                      <span title={s.tagText}>{s.tagText}</span>
                    </Tag>
                  )}
                </div>
              </div>
              {s.included ? (
                <Tag id={`searchDropdown-suggestion-addedTag-${s.tagText}`}>
                  {formatMessage({
                    defaultMessage: 'Added',
                    id: 'common.added'
                  })}
                </Tag>
              ) : (
                <ButtonUI
                  icon={isReplace ? 'fal fa-exchange-alt' : 'fal fa-plus'}
                  shape="link"
                  text={
                    isReplace
                      ? formatMessage({
                          defaultMessage: 'Replace',
                          id: 'common.replace'
                        })
                      : formatMessage({
                          defaultMessage: 'Add',
                          id: 'common.add'
                        })
                  }
                  type="tertiary"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </SearchDropdownStyled>
  );
}
